/**
 * Pause overlay (sc-2819, extended for sc-3014 leaderboard tab).
 *
 * Shown when the player presses Escape mid-cartridge. Pauses the runtime
 * loop, dims the cartridge's last rendered frame, and presents a
 * tabbed interface:
 *
 *   - PAUSE        — controls + resume / quit menu (original sc-2819 UX)
 *   - LEADERBOARD  — top-10 from /v1/scores/:cartridgeId (sc-3014)
 *
 * Left/Right rotate tabs, Up/Down navigate within a tab, Z/Enter
 * confirms. The overlay is canvas-rendered so it lives inside the
 * bezel/cabinet aesthetic.
 */

import type {
  ControlBinding,
  GameInstance,
  GameModule,
  Leaderboard,
  LeaderboardEntry,
  LeaderboardPeriod,
} from '@arcraz/arcadia-types';
import { ApiError, getLeaderboard } from '../api/client.js';
import { getVisibleCanvas, present } from './compositor.js';
import { getCabinetAudioBus } from './context.js';
import type { InputSource } from './loop.js';

// WIDTH / HEIGHT are read from g.canvas at each render call so the overlay
// adapts to whatever resolution the active cartridge declared (480x270 for
// pixel cartridges, 960x540 for vector). Without this, vector cartridges
// painted the overlay at a 480x270 origin into a 960x540 canvas — the
// scrim covered only the top-left quadrant and the centered panel ended
// up in the upper-left.

const SCRIM = 'rgba(4, 6, 13, 0.55)';
const PANEL_BG = 'rgba(10, 4, 24, 0.78)';
const PANEL_BORDER = '#ffea00';
const TITLE = '#ffea00';
const TAB_DIM = 'rgba(255, 234, 0, 0.4)';
const TAB_ACTIVE = '#ffea00';
const ROW_DIM = 'rgba(255, 234, 0, 0.4)';
const ROW_FOCUSED = '#ffea00';
const CONTROLS_LABEL = '#7af3c8';
const CONTROLS_KEY = '#00d9ff';
const CONTROLS_ACTION = '#ffffff';
const HINT = '#7af3c8';
const LB_RANK = '#7af3c8';
const LB_INITIALS = '#ffea00';
const LB_INITIALS_ANON = 'rgba(255, 234, 0, 0.45)';
const LB_SCORE = '#ffffff';
const LB_SCORE_ANON = 'rgba(255, 255, 255, 0.55)';
const LB_STATUS = 'rgba(122, 243, 200, 0.7)';
const LB_OWN_BG = 'rgba(122, 243, 200, 0.12)';
const LB_OWN_MARK = '#7af3c8';

const FALLBACK_CONTROLS: readonly ControlBinding[] = [
  { keys: 'ARROWS', action: 'MOVE' },
  { keys: 'Z', action: 'ACTION' },
];

export type PauseChoice = 'resume' | 'quit';

interface PauseMenuItem {
  readonly label: string;
  readonly choice: PauseChoice;
}

const PAUSE_MENU: readonly PauseMenuItem[] = [
  { label: 'RESUME', choice: 'resume' },
  { label: 'RETURN TO SELECTOR', choice: 'quit' },
];

type Tab = 'pause' | 'leaderboard';
const TABS: readonly Tab[] = ['pause', 'leaderboard'];

interface LeaderboardState {
  period: LeaderboardPeriod;
  status: 'idle' | 'loading' | 'loaded' | 'error';
  data: Leaderboard | null;
  error: string | null;
}

// --- Tap regions ----------------------------------------------------------
// Per-frame list of tappable rectangles in logical canvas coords.
// Same pattern selector + mode-picker use. sc-3290 phase 1.
interface TapRegion {
  x: number;
  y: number;
  w: number;
  h: number;
  action: TapAction;
}
type TapAction =
  | { kind: 'tab'; tab: Tab }
  | { kind: 'menu'; idx: number }
  | { kind: 'resume' }
  | { kind: 'lb-cycle' };
const tapRegions: TapRegion[] = [];

function clearTapRegions(): void {
  tapRegions.length = 0;
}
function pushTapRegion(region: TapRegion): void {
  tapRegions.push(region);
}
function hitTestPointer(
  evt: PointerEvent,
  visible: HTMLCanvasElement,
  logicalW: number,
  logicalH: number
): TapAction | null {
  const r = visible.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  const lx = ((evt.clientX - r.left) / r.width) * logicalW;
  const ly = ((evt.clientY - r.top) / r.height) * logicalH;
  for (const region of tapRegions) {
    if (
      lx >= region.x &&
      lx <= region.x + region.w &&
      ly >= region.y &&
      ly <= region.y + region.h
    ) {
      return region.action;
    }
  }
  return null;
}

/**
 * Run the pause overlay until the player picks an action.
 *
 * Caller is responsible for pausing the cartridge's runtime loop AND
 * calling `instance.pause()` BEFORE calling this — otherwise the
 * cartridge's update() will keep firing under the overlay.
 */
export function runPauseOverlay(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  module_: GameModule,
  instance: GameInstance
): Promise<PauseChoice> {
  let activeTab: Tab = 'pause';
  let pauseFocus = 0;
  let blink = 0;
  const audio = getCabinetAudioBus();

  // Touch input (sc-3290 phase 1). Same pattern as selector / mode-picker.
  // Important: the pause-button module also has a capture-phase listener
  // on the same canvas; if the player taps the pause button to OPEN this
  // overlay, the next pointerdown might still fire — but we attach AFTER
  // the pause button's capture listener and only react to taps that fall
  // inside our tap regions, so no false positives.
  const pendingTaps: TapAction[] = [];
  const visibleCanvas = getVisibleCanvas();
  const offscreenCanvas = ctx2d.canvas;
  const handlePointerDown = (evt: PointerEvent): void => {
    if (!evt.isPrimary) return;
    const action = hitTestPointer(
      evt,
      visibleCanvas,
      offscreenCanvas.width,
      offscreenCanvas.height
    );
    if (action) {
      pendingTaps.push(action);
      evt.preventDefault();
    }
  };
  visibleCanvas.addEventListener('pointerdown', handlePointerDown);
  const cleanup = (): void => {
    visibleCanvas.removeEventListener('pointerdown', handlePointerDown);
    clearTapRegions();
  };

  const lb: LeaderboardState = {
    period: 'all',
    status: 'idle',
    data: null,
    error: null,
  };

  // Lazy-load the leaderboard the first time the tab is shown for each
  // period. Caching it for the lifetime of the overlay is fine — the
  // player can re-open the pause menu to refresh.
  const loadLeaderboard = (period: LeaderboardPeriod): void => {
    lb.period = period;
    lb.status = 'loading';
    lb.data = null;
    lb.error = null;
    getLeaderboard(module_.id, {
      period,
      limit: 10,
      cartridgeVersion: module_.validatorVersion,
    })
      .then((data) => {
        // Late return after the player switched away is fine — we still
        // cache and render if they switch back to the same period.
        if (lb.period !== period) return;
        lb.status = 'loaded';
        lb.data = data;
      })
      .catch((err: unknown) => {
        if (lb.period !== period) return;
        lb.status = 'error';
        lb.error =
          err instanceof ApiError
            ? `${err.status} ${err.code}`
            : err instanceof Error
              ? err.message
              : 'UNKNOWN ERROR';
      });
  };

  return new Promise<PauseChoice>((resolve) => {
    const finish = (choice: PauseChoice): void => {
      cleanup();
      resolve(choice);
    };

    const tick = (): void => {
      const state = input.snapshot();

      // ---- Tab navigation: Left/Right ----
      if (state.justPressed.has('left')) {
        const idx = TABS.indexOf(activeTab);
        if (idx > 0) activeTab = TABS[idx - 1] ?? activeTab;
      } else if (state.justPressed.has('right')) {
        const idx = TABS.indexOf(activeTab);
        if (idx < TABS.length - 1) activeTab = TABS[idx + 1] ?? activeTab;
      }

      // ---- Per-tab input handling ----
      if (activeTab === 'pause') {
        if (state.justPressed.has('up') && pauseFocus > 0) {
          pauseFocus -= 1;
        } else if (
          state.justPressed.has('down') &&
          pauseFocus < PAUSE_MENU.length - 1
        ) {
          pauseFocus += 1;
        }

        if (state.justPressed.has('start') || state.justPressed.has('fireA')) {
          const item = PAUSE_MENU[pauseFocus] as PauseMenuItem;
          finish(item.choice);
          return;
        }
      } else {
        // Leaderboard tab. Load on first display (or after tab switch
        // back); Z cycles ALL → TODAY → DAILY → ALL.
        if (lb.status === 'idle') {
          loadLeaderboard(lb.period);
        }
        if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
          loadLeaderboard(nextPeriod(lb.period));
        }
      }

      // ---- Drain touch taps ----
      // Tap semantics:
      //   - Tap a tab → switch tabs
      //   - Tap a menu row → focus, then on second tap confirm. We use
      //     a single-tap-confirms model here because the pause menu is
      //     a binary RESUME/QUIT choice and "select-then-tap" feels
      //     overcautious. Same as a typical modal.
      //   - Tap LB-CYCLE → cycle leaderboard period
      //   - Tap BACK / RESUME glyph → resolve('resume')
      while (pendingTaps.length > 0) {
        const tap = pendingTaps.shift() as TapAction;
        if (tap.kind === 'tab') {
          activeTab = tap.tab;
        } else if (tap.kind === 'menu') {
          const item = PAUSE_MENU[tap.idx] as PauseMenuItem;
          finish(item.choice);
          return;
        } else if (tap.kind === 'lb-cycle') {
          loadLeaderboard(nextPeriod(lb.period));
        } else if (tap.kind === 'resume') {
          finish('resume');
          return;
        }
      }

      // Cabinet's Escape listener (see main.ts) toggles 'coin' on
      // request-pause; when already paused it means RESUME.
      if (state.justPressed.has('coin')) {
        finish('resume');
        return;
      }

      // Repaint each tick.
      instance.render(ctx2d);
      blink = (blink + 1) % 60;
      clearTapRegions();
      render(ctx2d, module_, activeTab, pauseFocus, blink, audio.getMuted(), lb);
      present();
      requestAnimationFrame(tick);
    };

    tick();
  });
}

function render(
  g: CanvasRenderingContext2D,
  module_: GameModule,
  activeTab: Tab,
  pauseFocus: number,
  blink: number,
  muted: boolean,
  lb: LeaderboardState
): void {
  const WIDTH = g.canvas.width;
  const HEIGHT = g.canvas.height;
  g.fillStyle = SCRIM;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  const panelW = 360;
  const panelH = 220;
  const panelX = WIDTH / 2 - panelW / 2;
  const panelY = HEIGHT / 2 - panelH / 2;

  g.fillStyle = PANEL_BG;
  g.fillRect(panelX, panelY, panelW, panelH);
  g.strokeStyle = PANEL_BORDER;
  g.lineWidth = 2;
  g.strokeRect(panelX + 0.5, panelY + 0.5, panelW - 1, panelH - 1);

  // Tab strip across the top of the panel.
  g.textAlign = 'center';
  g.textBaseline = 'top';
  g.font = 'bold 11px ui-monospace, monospace';
  const tabSpacing = 120;
  const tabsCenterY = panelY + 14;
  TABS.forEach((tab, i) => {
    const x = WIDTH / 2 + (i - (TABS.length - 1) / 2) * tabSpacing;
    const label = tab === 'pause' ? 'PAUSE' : 'LEADERBOARD';
    const active = tab === activeTab;
    g.fillStyle = active ? TAB_ACTIVE : TAB_DIM;
    g.fillText(label, x, tabsCenterY);
    if (active) {
      // Underline.
      const tw = g.measureText(label).width;
      g.fillRect(x - tw / 2, tabsCenterY + 14, tw, 1);
    }
    // Tap region — generous around the tab label.
    const tw = g.measureText(label).width;
    pushTapRegion({
      x: x - tw / 2 - 8,
      y: tabsCenterY - 4,
      w: tw + 16,
      h: 22,
      action: { kind: 'tab', tab },
    });
  });

  // Tappable BACK glyph in the top-left of the panel. Same visual
  // language as mode-picker's BACK. Lets touch users resume gameplay
  // without a keyboard Escape.
  g.textAlign = 'left';
  g.font = 'bold 10px ui-monospace, monospace';
  g.fillStyle = ROW_DIM;
  g.fillText('◂ BACK', panelX + 10, panelY + 10);
  pushTapRegion({
    x: panelX + 6,
    y: panelY + 4,
    w: 56,
    h: 18,
    action: { kind: 'resume' },
  });

  // Tab content.
  if (activeTab === 'pause') {
    renderPauseTab(g, module_, pauseFocus, blink, muted, panelX, panelY, panelW, panelH);
  } else {
    renderLeaderboardTab(g, module_, lb, panelX, panelY, panelW);
  }

  // Footer hint.
  g.font = '8px ui-monospace, monospace';
  g.fillStyle = HINT;
  g.textAlign = 'center';
  g.fillText(
    'LF/RT — TAB    ESC — RESUME',
    WIDTH / 2,
    panelY + panelH + 8
  );

  g.textAlign = 'left';
  g.textBaseline = 'top';
}

function renderPauseTab(
  g: CanvasRenderingContext2D,
  module_: GameModule,
  focusIndex: number,
  blink: number,
  muted: boolean,
  panelX: number,
  panelY: number,
  panelW: number,
  panelH: number
): void {
  const WIDTH = g.canvas.width;
  g.textAlign = 'center';
  g.fillStyle = TITLE;
  g.font = 'bold 14px ui-monospace, monospace';
  g.fillText(module_.title, WIDTH / 2, panelY + 36);

  // Controls listing — same content as the original overlay, but starts
  // lower to make room for the tab strip.
  const controls = module_.controls ?? FALLBACK_CONTROLS;
  g.textAlign = 'left';
  g.font = '8px ui-monospace, monospace';
  let cy = panelY + 60;
  g.fillStyle = CONTROLS_LABEL;
  g.fillText('CONTROLS', panelX + 20, cy);
  cy += 10;
  for (const c of controls) {
    g.fillStyle = CONTROLS_KEY;
    g.fillText(c.keys, panelX + 20, cy);
    g.fillStyle = CONTROLS_ACTION;
    g.textAlign = 'right';
    g.fillText(c.action, panelX + panelW - 20, cy);
    g.textAlign = 'left';
    cy += 10;
  }
  g.fillStyle = CONTROLS_KEY;
  g.fillText('M', panelX + 20, cy);
  g.fillStyle = muted ? '#ff8080' : CONTROLS_ACTION;
  g.textAlign = 'right';
  g.fillText(`MUTE AUDIO (${muted ? 'OFF' : 'ON'})`, panelX + panelW - 20, cy);
  g.textAlign = 'left';

  // RESUME / QUIT menu near the bottom.
  g.textAlign = 'center';
  g.font = 'bold 11px ui-monospace, monospace';
  const menuStartY = panelY + panelH - 50;
  for (let i = 0; i < PAUSE_MENU.length; i++) {
    const item = PAUSE_MENU[i] as PauseMenuItem;
    const focused = i === focusIndex;
    const my = menuStartY + i * 16;
    if (focused && blink < 40) {
      g.textAlign = 'left';
      g.fillStyle = ROW_FOCUSED;
      g.fillText('>', panelX + 50, my);
      g.textAlign = 'center';
    }
    g.fillStyle = focused ? ROW_FOCUSED : ROW_DIM;
    g.fillText(item.label, WIDTH / 2, my);
    // Tap region spans the panel-interior width minus inset.
    pushTapRegion({
      x: panelX + 30,
      y: my - 6,
      w: panelW - 60,
      h: 16,
      action: { kind: 'menu', idx: i },
    });
  }
}

function renderLeaderboardTab(
  g: CanvasRenderingContext2D,
  module_: GameModule,
  lb: LeaderboardState,
  panelX: number,
  panelY: number,
  panelW: number
): void {
  const WIDTH = g.canvas.width;
  g.textAlign = 'center';
  g.fillStyle = TITLE;
  g.font = 'bold 12px ui-monospace, monospace';
  g.fillText(module_.title, WIDTH / 2, panelY + 36);

  // Period sub-tabs (ALL TIME / TODAY / DAILY). Z cycles through.
  g.font = 'bold 9px ui-monospace, monospace';
  const periodY = panelY + 52;
  const labels: Array<{ label: string; period: LeaderboardPeriod }> = [
    { label: 'ALL TIME', period: 'all' },
    { label: 'TODAY', period: 'today' },
    { label: 'DAILY', period: 'daily' },
  ];
  const gap = 14;
  const widths = labels.map((l) => g.measureText(l.label).width);
  const totalW = widths.reduce((a, b) => a + b, 0) + gap * (labels.length - 1);
  let x = WIDTH / 2 - totalW / 2;
  g.textAlign = 'left';
  for (let i = 0; i < labels.length; i++) {
    const item = labels[i]!;
    g.fillStyle = lb.period === item.period ? TAB_ACTIVE : TAB_DIM;
    g.fillText(item.label, x, periodY);
    x += widths[i]! + gap;
  }

  // Body: either loading, error, or the top 10 rows.
  const bodyY = panelY + 72;
  if (lb.status === 'loading') {
    g.textAlign = 'center';
    g.fillStyle = LB_STATUS;
    g.font = '9px ui-monospace, monospace';
    g.fillText('LOADING…', WIDTH / 2, bodyY + 40);
    return;
  }
  if (lb.status === 'error') {
    g.textAlign = 'center';
    g.fillStyle = '#ff8080';
    g.font = '9px ui-monospace, monospace';
    g.fillText('FAILED TO LOAD', WIDTH / 2, bodyY + 30);
    g.fillStyle = LB_STATUS;
    g.font = '8px ui-monospace, monospace';
    g.fillText(lb.error ?? 'UNKNOWN', WIDTH / 2, bodyY + 44);
    g.fillText('Z TO RETRY', WIDTH / 2, bodyY + 58);
    return;
  }
  if (!lb.data || lb.data.scores.length === 0) {
    g.textAlign = 'center';
    g.fillStyle = LB_STATUS;
    g.font = '9px ui-monospace, monospace';
    g.fillText('NO SCORES YET — BE THE FIRST', WIDTH / 2, bodyY + 40);
    return;
  }

  // Top 10 rows. Compact layout: rank | mark | initials | score.
  // Anonymous rows render dimmed (no checkmark); authed rows render
  // bright with a small ✓; the signed-in player's own rows get a
  // tinted background + leading > marker.
  g.font = '9px ui-monospace, monospace';
  g.textAlign = 'left';
  const rowH = 11;
  const colRankX = panelX + 24;
  const colMarkX = panelX + 56;
  const colInitX = panelX + 76;
  const colScoreRightX = panelX + panelW - 24;
  const meId = lb.data.me?.playerId ?? null;
  lb.data.scores.slice(0, 10).forEach((entry: LeaderboardEntry, i: number) => {
    const ry = bodyY + i * rowH;
    const isMine = meId !== null && entry.playerId === meId;
    if (isMine) {
      g.fillStyle = LB_OWN_BG;
      g.fillRect(panelX + 16, ry - 1, panelW - 32, rowH);
      g.fillStyle = LB_OWN_MARK;
      g.fillText('>', panelX + 14, ry);
    }
    g.fillStyle = LB_RANK;
    g.fillText(`#${String(entry.rank).padStart(2, ' ')}`, colRankX, ry);
    // Verified mark — only authed rows.
    if (!entry.isAnonymous) {
      g.fillStyle = LB_OWN_MARK;
      g.fillText('✓', colMarkX, ry);
    }
    g.fillStyle = entry.isAnonymous ? LB_INITIALS_ANON : LB_INITIALS;
    g.fillText(entry.initials, colInitX, ry);
    g.fillStyle = entry.isAnonymous ? LB_SCORE_ANON : LB_SCORE;
    g.textAlign = 'right';
    g.fillText(formatScore(entry.score), colScoreRightX, ry);
    g.textAlign = 'left';
  });

  // Hint at the bottom of the tab body — Z toggles period.
  g.font = '8px ui-monospace, monospace';
  g.fillStyle = LB_STATUS;
  g.textAlign = 'center';
  g.fillText('Z — CYCLE ALL/TODAY/DAILY', WIDTH / 2, bodyY + 10 * rowH + 4);
}

function formatScore(score: number): string {
  return score.toLocaleString('en-US');
}

function nextPeriod(p: LeaderboardPeriod): LeaderboardPeriod {
  if (p === 'all') return 'today';
  if (p === 'today') return 'daily';
  return 'all';
}
